import { useCallback, useEffect, useRef, useState } from 'react'

// Copying to the clipboard: the one way every Copy button in the admin does
// it (owner 2026-10-07: Copy did nothing in Safari).
//
// Safari lets a page write the clipboard only while it is still handling the
// click (its user gesture). A write that starts after an `await` -- the link
// fetched first, a timer, a promise chain begun earlier -- is refused:
// navigator.clipboard.writeText rejects and execCommand('copy') returns
// false, while the old code went on to say "Copied" or "Copy this link". So:
//
//  - copyText(text) starts the write synchronously. Call it straight from
//    the click handler, before anything is awaited. It runs
//    document.execCommand('copy') on an off-screen copy of the text first:
//    synchronous, it says whether the copy happened, and Safari, Chrome and
//    Firefox all honour it inside a click. Only when that is refused does it
//    fall back to the async Clipboard API, started in the same tick.
//  - copyTextLater(promise) is for text still loading: Safari keeps the
//    gesture for a ClipboardItem whose data is a promise, so the write is
//    started inside the click and completes when the text arrives.
//
// Both resolve to whether the text really reached the clipboard. When it
// didn't, select the text (selectText) and ask for the shortcut
// (manualCopyHint): never say "Copied". useCopy does all of that for a
// button.

// The text, as an off-screen selection, copied by execCommand. A capturing
// `copy` listener sets the exact text as text/plain, whatever else is
// selected or focused; `beforecopy` is cancelled so WebKit enables the
// command. Focus and the person's own selection are put back afterwards.
const execCopy = text => {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function' || !document.body) return false

  const active = document.activeElement
  const selection = typeof window.getSelection === 'function' ? window.getSelection() : null
  const saved = []
  if (selection) for (let i = 0; i < selection.rangeCount; i++) saved.push(selection.getRangeAt(i))

  const holder = document.createElement('span')
  holder.textContent = text
  holder.setAttribute('aria-hidden', 'true')
  holder.setAttribute('data-clipboard-holder', '')
  Object.assign(holder.style, {
    position: 'fixed', top: '0', left: '0', opacity: '0', pointerEvents: 'none',
    whiteSpace: 'pre', userSelect: 'text', webkitUserSelect: 'text'
  })

  const onCopy = e => {
    if (!e.clipboardData) return
    e.preventDefault()
    e.stopImmediatePropagation()
    e.clipboardData.setData('text/plain', text)
  }
  const onBeforeCopy = e => e.preventDefault()

  let copied = false
  document.addEventListener('copy', onCopy, true)
  document.addEventListener('beforecopy', onBeforeCopy, true)
  document.body.appendChild(holder)
  try {
    // A focused field keeps the selection in itself; let it go while copying.
    if (active && active !== document.body && typeof active.blur === 'function' && /^(INPUT|TEXTAREA)$/.test(active.tagName)) active.blur()
    if (selection && typeof document.createRange === 'function') {
      const range = document.createRange()
      range.selectNodeContents(holder)
      selection.removeAllRanges()
      selection.addRange(range)
    }
    copied = document.execCommand('copy') === true
  } catch (e) {
    copied = false
  } finally {
    document.removeEventListener('copy', onCopy, true)
    document.removeEventListener('beforecopy', onBeforeCopy, true)
    holder.remove()
    if (selection) {
      selection.removeAllRanges()
      saved.forEach(range => selection.addRange(range))
    }
    if (active && active !== document.activeElement && typeof active.focus === 'function') active.focus({ preventScroll: true })
  }
  return copied
}

const asText = value => (value == null ? '' : String(value))

// The async Clipboard API's answer, never a throw.
const writeText = text => {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      return navigator.clipboard.writeText(text).then(() => true, () => false)
    }
  } catch (e) {
    // fall through
  }
  return Promise.resolve(false)
}

// Starts the copy now (call it straight from the click). Resolves to whether
// the text reached the clipboard.
export const copyText = value => {
  const text = asText(value)
  if (execCopy(text)) return Promise.resolve(true)
  return writeText(text)
}

// Text that is still loading (a promise): the clipboard write is started
// now, in the click, and finished when the text arrives. Resolves to
// { copied, text }; rejects with the load's own error when it fails.
export const copyTextLater = pending => {
  const text = Promise.resolve(pending).then(asText)
  let started = null
  try {
    if (typeof window !== 'undefined' && typeof window.ClipboardItem === 'function' &&
      navigator.clipboard && typeof navigator.clipboard.write === 'function') {
      const item = new window.ClipboardItem({ 'text/plain': text.then(t => new Blob([t], { type: 'text/plain' })) })
      started = navigator.clipboard.write([item]).then(() => true, () => false)
    }
  } catch (e) {
    started = null
  }
  return Promise.all([text, started || Promise.resolve(false)]).then(([t, copied]) =>
    copied ? { copied: true, text: t } : copyText(t).then(again => ({ copied: again, text: t })))
}

// Selects a field's value (focused, so the shortcut copies it) or any other
// element's text, for the person to copy themselves.
export const selectText = target => {
  const el = target && target.current !== undefined ? target.current : target
  if (!el) return
  if (/^(INPUT|TEXTAREA)$/.test(el.tagName)) {
    el.focus({ preventScroll: true })
    el.select()
    return
  }
  const selection = window.getSelection && window.getSelection()
  if (!selection || typeof document.createRange !== 'function') return
  const range = document.createRange()
  range.selectNodeContents(el)
  selection.removeAllRanges()
  selection.addRange(range)
}

const platformOf = () => {
  if (typeof navigator === 'undefined') return ''
  return (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent || ''
}

// What to press, on this platform.
export const manualCopyHint = (platform = platformOf()) =>
  /mac|iphone|ipad|ipod|ios/i.test(platform) ? 'Press ⌘C to copy' : 'Press Ctrl+C to copy'

const COPIED_MS = 1500
const MANUAL_MS = 8000

// A Copy button's state: copy(text, fallback) copies in the click; on
// success `copied` is true for a moment; when nothing was copied it selects
// `fallback` (a ref or an element) and `manual` is true, with `hint` to show.
export const useCopy = () => {
  const [state, setState] = useState('idle')
  const timer = useRef(null)
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const settle = useCallback((next, ms) => {
    if (!live.current) return
    setState(next)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => { if (live.current) setState('idle') }, ms)
  }, [])

  const copy = useCallback((text, fallback) =>
    copyText(text).then(copied => {
      if (copied) settle('copied', COPIED_MS)
      else {
        if (live.current) selectText(fallback)
        settle('manual', MANUAL_MS)
      }
      return copied
    }), [settle])

  return { copy, state, copied: state === 'copied', manual: state === 'manual', hint: manualCopyHint() }
}
